// Chemistry → Section 5: Classifying Materials (KS3, sourced from the
// Grade9Ready science PDF). Same rich-content template as the Biology
// section files. Authored topics from `authoredTopics.json` override
// any topic with a matching id at runtime.
import authored from './authoredTopics.json';

const AUTHORED_KEY = 'chemistry/classifying-materials';
const AUTHORED = (authored && authored[AUTHORED_KEY]) || [];
const AUTHORED_BY_ID = Object.fromEntries(AUTHORED.map((t) => [t.id, t]));

// =====================================================================
// 5.1  STATES OF MATTER
// =====================================================================
const STATES_TOPIC = {
  id: 'chem-5.1',
  title: 'States of Matter',
  slug: 'states-of-matter',
  pageRef: 'Section 5 · Topic 1',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Solid, liquid, gas',
        body:
          'There are three common states of matter. Solids hold a fixed shape and a fixed volume. Liquids hold a fixed volume but take the shape of their container. Gases fill any container completely — they have neither a fixed shape nor a fixed volume. The state depends on temperature and pressure.',
        check: {
          question: 'Which state has a fixed volume but takes the shape of its container?',
          options: ['Solid', 'Liquid', 'Gas', 'Plasma'],
          answer: 'Liquid',
          explainer: 'Liquids keep volume but flow into the shape they are poured into.',
        },
      },
      {
        heading: 'Properties side-by-side',
        body:
          'Density: solids are usually densest, gases the least dense. Compressibility: gases compress easily, liquids hardly at all, solids almost not. Flow: solids do not flow, liquids and gases do. These properties are explained by how close the particles are and how freely they move — covered in detail under Particle Theory.',
        check: {
          question: 'Which state is the EASIEST to compress?',
          options: ['Solid', 'Liquid', 'Gas', 'They are all the same'],
          answer: 'Gas',
          explainer: 'Gas particles are far apart, so squeezing them into a smaller volume is easy.',
        },
      },
      {
        heading: 'Real-world examples',
        body:
          'Solid: ice, copper, salt. Liquid: water, mercury, petrol. Gas: oxygen, steam, carbon dioxide. The same substance can be all three depending on temperature: water is solid below 0 °C, liquid 0–100 °C, and a gas above 100 °C (at normal atmospheric pressure).',
        check: {
          question: 'Above 100 °C at normal pressure, water is…',
          options: ['Solid', 'Liquid', 'Gas', 'Plasma'],
          answer: 'Gas',
          explainer: 'Past the boiling point, water becomes water vapour (steam).',
        },
      },
    ],
    keyPoints: [
      'Solid: fixed shape AND fixed volume',
      'Liquid: fixed volume, shape of container',
      'Gas: no fixed shape, no fixed volume',
      'Gases compress easily; solids and liquids do not',
      'Same substance can be solid, liquid or gas depending on temperature',
    ],
    workedExamples: [
      {
        problem: 'Classify each as solid, liquid or gas at room temperature: oxygen, ice, mercury, copper, steam.',
        steps: [
          { step: 1, instruction: 'Oxygen', working: 'Air component → gas' },
          { step: 2, instruction: 'Ice', working: 'Water below 0 °C → solid' },
          { step: 3, instruction: 'Mercury', working: 'A metal liquid at room temperature → liquid' },
          { step: 4, instruction: 'Copper', working: 'A metal at room temp → solid' },
          { step: 5, instruction: 'Steam', working: 'Water above 100 °C → gas' },
        ],
        answer: 'Gas: oxygen, steam. Liquid: mercury. Solid: ice, copper.',
        commonMistake: 'Calling mercury a solid — at room temperature mercury is a liquid metal.',
      },
    ],
    whyItWorks:
      'Three states is a useful starting model. There are actually more (plasma, supercritical fluids), but for the GCSE level, "solid / liquid / gas" handles 99% of everyday chemistry.',
  },
  glossary: [
    { term: 'Solid', definition: 'A state with fixed shape and fixed volume.' },
    { term: 'Liquid', definition: 'A state with fixed volume but takes the shape of its container.' },
    { term: 'Gas', definition: 'A state with neither fixed shape nor fixed volume.' },
    { term: 'Compressible', definition: 'Able to be squashed into a smaller volume — gases are very compressible.' },
    { term: 'Density', definition: 'How much mass is packed into a given volume (mass ÷ volume).' },
  ],
  examQuestion: {
    prompt:
      '(a) State THREE differences between liquids and gases. (b) Explain why a gas is much easier to compress than a liquid. (c) Predict the state of water at -5 °C and at 120 °C. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Liquids keep their volume; gases do not',
      'Liquids are much denser than gases',
      'Gases compress easily; liquids do not',
      'Gas particles are far apart so there is space to squeeze them',
      '-5 °C: solid (ice). 120 °C: gas (steam).',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-5.1.w1', type: 'text', question: 'Name the state with no fixed shape or volume.', answer: 'gas', hint1: '3 letters', hint2: 'You breathe it', hint3: 'Like air' },
      { id: 'chem-5.1.w2', type: 'choice', question: 'Which keeps a fixed shape?', options: ['Gas', 'Liquid', 'Solid', 'None'], answer: 'Solid', hint1: 'Hardest', hint2: '5 letters', hint3: 'Like ice or wood' },
      { id: 'chem-5.1.w3', type: 'choice', question: 'Mercury at room temperature is a…', options: ['Solid', 'Liquid', 'Gas', 'Plasma'], answer: 'Liquid', hint1: 'Old thermometer fluid', hint2: 'Silver and shiny', hint3: 'A metal that flows' },
    ],
    level1: [
      { id: 'chem-5.1.1', type: 'choice', question: 'Which state is hardest to compress?', options: ['Gas', 'Liquid', 'Solid', 'They are equal'], answer: 'Solid', hint1: 'Densest', hint2: 'Particles touching', hint3: '5 letters' },
      { id: 'chem-5.1.2', type: 'text', question: 'Water above 100 °C at normal pressure becomes a ___ .', answer: 'gas', hint1: '3 letters', hint2: 'Steam', hint3: 'Vapour' },
      { id: 'chem-5.1.3', type: 'choice', question: 'Which has the lowest density?', options: ['Gas', 'Liquid', 'Solid', 'All are the same'], answer: 'Gas', hint1: 'Particles far apart', hint2: 'Less mass per volume', hint3: '3 letters' },
    ],
    level2: [
      { id: 'chem-5.1.4', type: 'text', question: 'Ice melts at ___ °C (at normal pressure).', answer: '0', hint1: 'Single digit', hint2: 'Freezing point', hint3: 'Zero' },
      { id: 'chem-5.1.5', type: 'choice', question: 'Pouring liquid into a different container — what changes?', options: ['Volume', 'Shape', 'Mass', 'Density'], answer: 'Shape', hint1: 'Volume stays', hint2: 'Takes container form', hint3: 'Surface curves' },
    ],
    level3: [
      { id: 'chem-5.1.6', type: 'choice', question: 'Why can gases fill any container?', options: ['Particles attract each other', 'Particles move randomly with weak attractions', 'Particles are huge', 'Particles repel'], answer: 'Particles move randomly with weak attractions', hint1: 'Linked to Particle Theory', hint2: 'Free movement', hint3: 'Random motion' },
      { id: 'chem-5.1.7', type: 'text', question: 'Boiling point of water at normal pressure is ___ °C.', answer: '100', hint1: 'Three digits', hint2: 'Triple zero ending', hint3: 'Steam appears' },
    ],
  },
};

// =====================================================================
// 5.2  PARTICLE THEORY
// =====================================================================
const PARTICLE_THEORY_TOPIC = {
  id: 'chem-5.2',
  title: 'Particle Theory',
  slug: 'particle-theory',
  pageRef: 'Section 5 · Topic 2',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Particles in solids, liquids and gases',
        body:
          'In solids, particles are packed tightly in a regular pattern and only vibrate on the spot. In liquids, particles are still close but in random positions and can flow past each other. In gases, particles are far apart and move randomly at high speed. The differences in particle arrangement explain every physical property of the three states.',
        check: {
          question: 'In which state do particles vibrate on the spot?',
          options: ['Solid', 'Liquid', 'Gas', 'All three'],
          answer: 'Solid',
          explainer: 'In a solid, the particles are locked into place by strong attractive forces — they wobble but cannot move past each other.',
        },
      },
      {
        heading: 'Why properties make sense',
        body:
          'Solids do not flow because particles cannot move past each other. Liquids flow because particles can roll over each other. Gases compress because there is empty space between particles. Increasing temperature gives every particle more kinetic energy — they move/vibrate faster, which is why heated solids and liquids expand.',
        check: {
          question: 'Why do gases expand when heated?',
          options: ['New particles are made', 'Particles move faster and further apart', 'Particles change shape', 'Particles disappear'],
          answer: 'Particles move faster and further apart',
          explainer: 'Heat = more kinetic energy = particles fly further before bumping into each other.',
        },
      },
      {
        heading: 'Diffusion and Brownian motion',
        body:
          'Diffusion is the spreading of particles from a high concentration to a low concentration. It happens fastest in gases (perfume across a room) and is slow in liquids. Brownian motion is the visible jiggling of small particles (e.g. smoke in air) caused by being knocked by faster, invisible particles around them — direct evidence that gas particles really do move randomly.',
        check: {
          question: 'Diffusion is fastest in…',
          options: ['Solids', 'Liquids', 'Gases', 'Plasma'],
          answer: 'Gases',
          explainer: 'Gas particles move fastest and have most empty space to spread through.',
        },
      },
    ],
    keyPoints: [
      'Solid: particles in fixed regular pattern, vibrate on the spot',
      'Liquid: particles close but random; flow past each other',
      'Gas: particles far apart, fast and random',
      'Heating = more kinetic energy = faster motion',
      'Diffusion: fastest in gases, slow in liquids',
      'Brownian motion = evidence that gas particles really move',
    ],
    workedExamples: [
      {
        problem: 'A perfume bottle is opened across a room. Predict what happens and explain using particle theory.',
        steps: [
          { step: 1, instruction: 'What enters the air', working: 'Perfume particles evaporate into the air' },
          { step: 2, instruction: 'How do they spread', working: 'Particles move randomly at high speed' },
          { step: 3, instruction: 'Result', working: 'They diffuse from high (near bottle) to low (across room) concentration' },
        ],
        answer: 'Perfume particles diffuse through the air; eventually you smell them everywhere.',
        commonMistake: 'Saying the particles "fly in a straight line" — they are knocked in random directions all the time.',
      },
    ],
    whyItWorks:
      'Particle theory is the foundation of physical chemistry. Every state-related property (melting, boiling, dissolving, compressing) can be explained by what the tiny particles are doing.',
  },
  glossary: [
    { term: 'Particle theory', definition: 'A model that says all matter is made of tiny particles in motion.' },
    { term: 'Kinetic energy', definition: 'The energy of moving particles — increases with temperature.' },
    { term: 'Diffusion', definition: 'Spreading of particles from high to low concentration.' },
    { term: 'Brownian motion', definition: 'The visible random jiggling of small particles caused by being hit by smaller, faster particles.' },
    { term: 'Vibrate', definition: 'To wobble in place — what particles in a solid do.' },
  ],
  examQuestion: {
    prompt:
      '(a) Use particle theory to explain why gases compress easily. (b) Use particle theory to explain why a liquid flows but a solid does not. (c) Define diffusion and state which state of matter it is fastest in. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Gas particles are far apart with empty space between',
      'So they can be pushed closer together',
      'In a liquid, particles can move past each other; in a solid they are locked in place',
      'Diffusion = spreading of particles from high to low concentration',
      'Fastest in gases',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-5.2.w1', type: 'text', question: 'Particles in a solid only ___ on the spot.', answer: 'vibrate', hint1: '7 letters', hint2: 'Wobble', hint3: 'Starts with v' },
      { id: 'chem-5.2.w2', type: 'choice', question: 'Diffusion is fastest in…', options: ['Solids', 'Liquids', 'Gases', 'They are the same'], answer: 'Gases', hint1: 'Particles move fastest', hint2: 'Lots of empty space', hint3: 'Spreads quickest' },
      { id: 'chem-5.2.w3', type: 'choice', question: 'Heating particles makes them…', options: ['Slower', 'Faster', 'Same speed', 'Disappear'], answer: 'Faster', hint1: 'More kinetic energy', hint2: 'Speeds up motion', hint3: 'Hot = busy' },
    ],
    level1: [
      { id: 'chem-5.2.1', type: 'choice', question: 'Why can a gas be compressed?', options: ['Particles disappear', 'There is space between particles', 'Particles get smaller', 'Particles freeze'], answer: 'There is space between particles', hint1: 'Empty gaps', hint2: 'Push closer', hint3: 'Far apart' },
      { id: 'chem-5.2.2', type: 'text', question: 'Random motion of particles seen under a microscope is ___ motion.', answer: 'Brownian', hint1: '8 letters', hint2: 'Named after a scientist', hint3: 'Capital B' },
      { id: 'chem-5.2.3', type: 'choice', question: 'In a liquid, particles…', options: ['Are fixed in place', 'Roll past each other', 'Are far apart', 'Vibrate only'], answer: 'Roll past each other', hint1: 'Allows flow', hint2: 'Still close', hint3: 'Random arrangement' },
    ],
    level2: [
      { id: 'chem-5.2.4', type: 'text', question: 'The energy a particle has because of its motion is its ___ energy.', answer: 'kinetic', hint1: '7 letters', hint2: 'Speed-related', hint3: 'Starts with k' },
      { id: 'chem-5.2.5', type: 'choice', question: 'Why does perfume reach the back of a room?', options: ['Photosynthesis', 'Diffusion of particles', 'Static charge', 'Gravity'], answer: 'Diffusion of particles', hint1: 'Random motion', hint2: 'High to low concentration', hint3: 'Spreading' },
    ],
    level3: [
      { id: 'chem-5.2.6', type: 'choice', question: 'A balloon left in the sun gets bigger. Why?', options: ['New particles appear', 'Particles speed up and push outwards harder', 'Air leaves the balloon', 'Rubber stretches randomly'], answer: 'Particles speed up and push outwards harder', hint1: 'Heating effect', hint2: 'More kinetic energy', hint3: 'Increased pressure' },
      { id: 'chem-5.2.7', type: 'text', question: 'Heating gives particles more ___ energy.', answer: 'kinetic', hint1: '7 letters', hint2: 'Linked to speed', hint3: 'Starts with k' },
    ],
  },
};

// =====================================================================
// 5.3  CHANGES OF STATE
// =====================================================================
const CHANGES_OF_STATE_TOPIC = {
  id: 'chem-5.3',
  title: 'Changes of State',
  slug: 'changes-of-state',
  pageRef: 'Section 5 · Topic 3',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'The six change names',
        body:
          'Solid → liquid is melting. Liquid → gas is evaporating (slow at the surface, any temperature) or boiling (fast, at the boiling point). Gas → liquid is condensing. Liquid → solid is freezing. Solid → gas (skipping liquid) is sublimation, e.g. solid CO₂ "dry ice" sublimes straight to a gas.',
        check: {
          question: 'The change from a gas to a liquid is called…',
          options: ['Melting', 'Boiling', 'Condensing', 'Subliming'],
          answer: 'Condensing',
          explainer: 'Examples: water vapour on a cold window pane condenses into droplets.',
        },
      },
      {
        heading: 'Reversible — and physical',
        body:
          'All changes of state are physical (no new substance is made) and reversible. Cool water vapour and you get water back. Freeze water and you get ice — exactly the same molecules. The mass is conserved: 100 g of ice melts into 100 g of water. Mass is NEVER lost during a state change.',
        check: {
          question: '50 g of ice fully melts. How much water is produced?',
          options: ['Less than 50 g', 'Exactly 50 g', 'More than 50 g', 'Cannot tell'],
          answer: 'Exactly 50 g',
          explainer: 'Mass is conserved during physical changes.',
        },
      },
      {
        heading: 'Heating curves',
        body:
          'When ice is heated, its temperature rises until it hits 0 °C — then it stops rising while the ice melts (energy goes into separating particles, not warming them). Once the ice has fully melted, the water heats up to 100 °C — and again the temperature stops rising while the water boils. The flat sections on a heating curve are at the melting and boiling points.',
        check: {
          question: 'On a heating graph, why does the temperature stay constant during melting?',
          options: ['Energy is lost to surroundings', 'Energy goes into breaking the bonds between particles', 'No more energy is being added', 'Particles disappear'],
          answer: 'Energy goes into breaking the bonds between particles',
          explainer: 'During a state change, energy is used to separate particles — not to raise temperature.',
        },
      },
    ],
    keyPoints: [
      'Solid → liquid: melting',
      'Liquid → gas: evaporating / boiling',
      'Gas → liquid: condensing',
      'Liquid → solid: freezing',
      'Solid → gas: subliming',
      'All physical, all reversible, mass conserved',
      'Heating curve: flat at melting + boiling points',
    ],
    workedExamples: [
      {
        problem: 'Identify each change: (i) ice → water, (ii) water → steam, (iii) steam → water, (iv) dry ice → CO₂ gas.',
        steps: [
          { step: 1, instruction: 'ice → water', working: 'Solid → liquid → melting' },
          { step: 2, instruction: 'water → steam', working: 'Liquid → gas → boiling/evaporating' },
          { step: 3, instruction: 'steam → water', working: 'Gas → liquid → condensing' },
          { step: 4, instruction: 'dry ice → CO₂ gas', working: 'Solid → gas → sublimation' },
        ],
        answer: '(i) melting, (ii) boiling/evaporating, (iii) condensing, (iv) sublimation.',
        commonMistake: 'Confusing "evaporation" with "boiling" — they are both liquid→gas, but boiling happens at the boiling point and throughout the liquid, while evaporation is slow at the surface at any temperature.',
      },
    ],
    whyItWorks:
      'A change of state is a tug-of-war between attractive forces holding particles together and the kinetic energy trying to break them apart. Heat tilts the balance towards motion (so things melt then boil); cooling tilts it towards bonding (so things condense then freeze).',
  },
  glossary: [
    { term: 'Melting', definition: 'Solid → liquid.' },
    { term: 'Boiling', definition: 'Liquid → gas at the boiling point throughout the liquid.' },
    { term: 'Evaporation', definition: 'Liquid → gas at the surface only, at any temperature.' },
    { term: 'Condensing', definition: 'Gas → liquid.' },
    { term: 'Freezing', definition: 'Liquid → solid.' },
    { term: 'Sublimation', definition: 'Solid → gas, skipping the liquid state.' },
    { term: 'Conservation of mass', definition: 'Total mass before = total mass after a physical change.' },
  ],
  examQuestion: {
    prompt:
      '(a) Name the change of state for: (i) water vapour to liquid water, (ii) iron melting in a foundry, (iii) dry ice (solid CO₂) becoming a gas. (b) Explain, using particles, why heating a solid eventually makes it melt. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      '(i) condensation / condensing',
      '(ii) melting',
      '(iii) sublimation / subliming',
      'Heating gives particles more kinetic energy / they vibrate more',
      'Eventually the vibrations are strong enough to break the bonds between particles → liquid forms',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-5.3.w1', type: 'text', question: 'Solid to liquid is called ___ .', answer: 'melting', hint1: '7 letters', hint2: 'Ice → water', hint3: 'Starts with m' },
      { id: 'chem-5.3.w2', type: 'choice', question: 'Steam → water is…', options: ['Boiling', 'Condensing', 'Melting', 'Subliming'], answer: 'Condensing', hint1: 'Cooling', hint2: 'On a cold window', hint3: '10 letters' },
      { id: 'chem-5.3.w3', type: 'choice', question: 'Solid CO₂ → CO₂ gas is…', options: ['Condensing', 'Boiling', 'Sublimation', 'Melting'], answer: 'Sublimation', hint1: 'Skips liquid', hint2: 'Dry ice', hint3: 'Starts with s' },
    ],
    level1: [
      { id: 'chem-5.3.1', type: 'choice', question: 'Liquid → solid is…', options: ['Melting', 'Freezing', 'Boiling', 'Subliming'], answer: 'Freezing', hint1: 'Opposite of melting', hint2: 'Water → ice', hint3: '8 letters' },
      { id: 'chem-5.3.2', type: 'number', question: '60 g of ice fully melts. Mass of water?', answer: '60', hint1: 'Mass conserved', hint2: 'Same as start', hint3: 'Two digits' },
      { id: 'chem-5.3.3', type: 'text', question: 'Slow liquid → gas at the surface only is called ___ .', answer: 'evaporation', hint1: '11 letters', hint2: 'Not boiling', hint3: 'Surface only' },
    ],
    level2: [
      { id: 'chem-5.3.4', type: 'choice', question: 'On a heating curve, the flat parts represent…', options: ['No heat being added', 'Changes of state', 'Cooling', 'Errors'], answer: 'Changes of state', hint1: 'Temperature constant', hint2: 'Melting + boiling', hint3: 'Energy breaks bonds' },
      { id: 'chem-5.3.5', type: 'choice', question: 'During melting, the temperature stays constant because…', options: ['No energy is being added', 'Energy is lost', 'Energy goes into breaking bonds, not raising temperature', 'Particles freeze'], answer: 'Energy goes into breaking bonds, not raising temperature', hint1: 'Energy use', hint2: 'Latent heat', hint3: 'Bonds breaking' },
    ],
    level3: [
      { id: 'chem-5.3.6', type: 'choice', question: 'Why is evaporation possible at room temperature?', options: ['All particles are at the same speed', 'Some surface particles are fast enough to escape', 'The air is hot', 'Liquid expands'], answer: 'Some surface particles are fast enough to escape', hint1: 'Not all particles same speed', hint2: 'Surface effect', hint3: 'Above-average kinetic energy' },
      { id: 'chem-5.3.7', type: 'text', question: 'Mass before = mass after. This rule is called conservation of ___ .', answer: 'mass', hint1: '4 letters', hint2: 'Cannot be created or destroyed', hint3: 'Starts with m' },
    ],
  },
};

// =====================================================================
// 5.4  ATOMS, ELEMENTS AND COMPOUNDS
// =====================================================================
const ATOMS_TOPIC = {
  id: 'chem-5.4',
  title: 'Atoms, Elements and Compounds',
  slug: 'atoms-elements-compounds',
  pageRef: 'Section 5 · Topic 4',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Atoms',
        body:
          'An atom is the smallest part of an element that still has the properties of that element. Atoms are tiny — about 0.0000001 mm across. Every atom has a small dense nucleus (protons + neutrons) surrounded by orbiting electrons. There are roughly 100 different types of atom in the universe.',
        check: {
          question: 'The smallest part of an element that still has its properties is a(n)…',
          options: ['Cell', 'Atom', 'Compound', 'Mixture'],
          answer: 'Atom',
          explainer: 'Each element is made of just one type of atom.',
        },
      },
      {
        heading: 'Elements vs compounds',
        body:
          'An element is a substance made of only ONE type of atom (e.g. oxygen O₂, gold Au, carbon C). A compound is two or more DIFFERENT types of atom chemically bonded together (e.g. water H₂O, salt NaCl, carbon dioxide CO₂). Compounds have very different properties from the elements they came from. Sodium is a violent metal, chlorine is a poisonous gas — but joined together they make table salt.',
        check: {
          question: 'Water (H₂O) is an example of a(n)…',
          options: ['Element', 'Compound', 'Mixture', 'Atom'],
          answer: 'Compound',
          explainer: 'Two different atoms (H and O) chemically bonded.',
        },
      },
      {
        heading: 'Reading chemical formulae',
        body:
          'In a formula like H₂O, the LETTERS show which atoms (H = hydrogen, O = oxygen) and the SUBSCRIPT NUMBERS show how many of each atom (2 hydrogens, 1 oxygen — implied). In CO₂ there is 1 carbon and 2 oxygens. Larger molecules: glucose C₆H₁₂O₆ = 6 carbons, 12 hydrogens and 6 oxygens.',
        check: {
          question: 'How many atoms in total in CO₂?',
          options: ['1', '2', '3', '4'],
          answer: '3',
          explainer: '1 carbon + 2 oxygens = 3 atoms.',
        },
      },
    ],
    keyPoints: [
      'Atom = smallest part of an element',
      'Element = ONE type of atom',
      '~100 different elements exist',
      'Compound = TWO or more types of atom chemically bonded',
      'Formula letters = elements; subscripts = number of each',
      'Compounds have new properties (Na + Cl₂ → table salt)',
    ],
    workedExamples: [
      {
        problem: 'Classify as element, compound or neither: O₂, H₂O, CO₂, gold (Au), the air.',
        steps: [
          { step: 1, instruction: 'O₂', working: 'Two oxygen atoms — same type → element' },
          { step: 2, instruction: 'H₂O', working: 'H + O bonded → compound' },
          { step: 3, instruction: 'CO₂', working: 'C + O bonded → compound' },
          { step: 4, instruction: 'Gold (Au)', working: 'One type of atom → element' },
          { step: 5, instruction: 'Air', working: 'Mixture of N₂, O₂, CO₂ etc. → mixture (not bonded)' },
        ],
        answer: 'Elements: O₂, gold. Compounds: H₂O, CO₂. Mixture: air.',
        commonMistake: 'Calling air a compound — it\u2019s a mixture; the gases are not chemically bonded together.',
      },
    ],
    whyItWorks:
      'All matter on Earth is built from a small set of building blocks: ~100 elements. Combining them in different ways gives literally millions of compounds — including every substance in your body and your phone.',
  },
  glossary: [
    { term: 'Atom', definition: 'The smallest unit of an element with the element\u2019s properties.' },
    { term: 'Element', definition: 'A substance made of one type of atom only.' },
    { term: 'Compound', definition: 'Two or more elements chemically bonded.' },
    { term: 'Formula', definition: 'A short way of writing a substance using element symbols and numbers.' },
    { term: 'Molecule', definition: 'Two or more atoms chemically bonded together (can be of an element or compound).' },
  ],
  examQuestion: {
    prompt:
      '(a) Define element and compound. (b) State how many atoms of each element are in glucose, C₆H₁₂O₆. (c) Sodium is a metal that reacts with chlorine, a green gas, to make table salt. Explain why salt is so different from sodium and chlorine. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Element = one type of atom',
      'Compound = two or more elements chemically bonded',
      '6 carbons, 12 hydrogens, 6 oxygens',
      'Salt is a compound — different bonding',
      'Compounds have very different properties from their constituent elements',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-5.4.w1', type: 'text', question: 'A substance made of one type of atom is called an ___ .', answer: 'element', hint1: '7 letters', hint2: 'Periodic table entry', hint3: 'Pure single-atom substance' },
      { id: 'chem-5.4.w2', type: 'choice', question: 'H₂O is an example of a…', options: ['Element', 'Compound', 'Mixture', 'Atom'], answer: 'Compound', hint1: 'Two elements bonded', hint2: 'Water', hint3: '8 letters' },
      { id: 'chem-5.4.w3', type: 'number', question: 'How many oxygen atoms in H₂SO₄?', answer: '4', hint1: 'Subscript next to O', hint2: 'Single digit', hint3: 'Sulfuric acid' },
    ],
    level1: [
      { id: 'chem-5.4.1', type: 'choice', question: 'Air is best classified as a…', options: ['Compound', 'Element', 'Mixture', 'Single atom'], answer: 'Mixture', hint1: 'Many gases not bonded', hint2: 'Can be separated by physical means', hint3: '7 letters' },
      { id: 'chem-5.4.2', type: 'text', question: 'How many different elements exist in nature? (approx)', answer: '100', hint1: 'Three digits', hint2: 'Round number', hint3: 'About 90 natural + manmade' },
    ],
    level2: [
      { id: 'chem-5.4.3', type: 'number', question: 'Total atoms in CaCO₃?', answer: '5', hint1: '1 Ca + 1 C + 3 O', hint2: 'Single digit', hint3: 'Sum subscripts' },
      { id: 'chem-5.4.4', type: 'choice', question: 'Why do compounds have new properties compared to their elements?', options: ['New atoms appear', 'Bonds change how atoms behave', 'It is random', 'They have more mass'], answer: 'Bonds change how atoms behave', hint1: 'Chemistry of bonding', hint2: 'New structure', hint3: 'Atoms behave together' },
    ],
    level3: [
      { id: 'chem-5.4.5', type: 'choice', question: 'Which is an element?', options: ['NaCl', 'O₂', 'H₂O', 'CO₂'], answer: 'O₂', hint1: 'Same atom only', hint2: 'Diatomic gas', hint3: 'You breathe it' },
      { id: 'chem-5.4.6', type: 'text', question: 'A formula H₂SO₄ contains H, S and ___ .', answer: 'O', hint1: 'Single letter', hint2: 'Element symbol', hint3: 'Oxygen' },
    ],
  },
};

// =====================================================================
// 5.5  MIXTURES
// =====================================================================
const MIXTURES_TOPIC = {
  id: 'chem-5.5',
  title: 'Mixtures',
  slug: 'mixtures',
  pageRef: 'Section 5 · Topic 5',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Pure substance vs mixture',
        body:
          'A pure substance is a single element OR a single compound — every particle is the same. A mixture is two or more substances physically together but NOT chemically bonded. Mixtures keep their original properties: a sand-and-salt mixture still tastes salty and looks gritty. You can usually separate a mixture using a physical method (no chemical reaction needed).',
        check: {
          question: 'Which of these is a pure substance?',
          options: ['Air', 'Sea water', 'Gold ring', 'Mud'],
          answer: 'Gold ring',
          explainer: 'Pure gold is one element — every atom is gold. The others are mixtures.',
        },
      },
      {
        heading: 'Solutions, suspensions and emulsions',
        body:
          'A solution forms when a solute (e.g. salt) dissolves into a solvent (e.g. water) to make a clear liquid. A suspension contains tiny solid bits floating in a liquid (e.g. muddy water) — they slowly settle. An emulsion is droplets of one liquid spread through another (e.g. milk = fat droplets in water).',
        check: {
          question: 'Salt water is an example of a…',
          options: ['Solution', 'Suspension', 'Emulsion', 'Compound'],
          answer: 'Solution',
          explainer: 'Salt dissolves completely in water to give a clear liquid.',
        },
      },
      {
        heading: 'Identifying purity',
        body:
          'A pure substance has a single, sharp melting and boiling point. A mixture melts/boils over a range of temperatures because each substance in the mixture changes state at its own temperature. Salt melts at 801 °C exactly; salt water doesn\u2019t have a single boiling point — it slowly boils up over a range as the water leaves first.',
        check: {
          question: 'A mixture melts and boils over a range because…',
          options: ['Particles are different', 'Each substance has its own melting/boiling point', 'It is impure on purpose', 'No reason'],
          answer: 'Each substance has its own melting/boiling point',
          explainer: 'Mixtures are not single substances, so they don\u2019t have a single sharp melting/boiling point.',
        },
      },
    ],
    keyPoints: [
      'Pure substance = element OR compound',
      'Mixture = NOT chemically bonded; physically together',
      'Solution = solute dissolved in solvent (clear)',
      'Suspension = particles floating, settle out',
      'Emulsion = droplets of one liquid in another',
      'Pure: sharp melting/boiling point. Mixture: range.',
    ],
    workedExamples: [
      {
        problem: 'Classify as pure substance or mixture: (i) distilled water, (ii) salt water, (iii) gold, (iv) mud.',
        steps: [
          { step: 1, instruction: '(i) distilled water', working: 'Only H₂O molecules → pure (compound)' },
          { step: 2, instruction: '(ii) salt water', working: 'NaCl + H₂O, not bonded → mixture (solution)' },
          { step: 3, instruction: '(iii) gold', working: 'One type of atom → pure (element)' },
          { step: 4, instruction: '(iv) mud', working: 'Soil particles in water → mixture (suspension)' },
        ],
        answer: 'Pure: distilled water, gold. Mixtures: salt water, mud.',
        commonMistake: 'Calling salt water a compound — salt and water are not chemically bonded; they are mixed.',
      },
    ],
    whyItWorks:
      'The pure-substance test is a sharp melting/boiling point. That\u2019s why drug companies are obsessed with melting points — a sharp value confirms purity.',
  },
  glossary: [
    { term: 'Pure substance', definition: 'A single element or single compound.' },
    { term: 'Mixture', definition: 'Two or more substances physically together, not chemically bonded.' },
    { term: 'Solution', definition: 'A solute dissolved in a solvent — fully clear.' },
    { term: 'Solute', definition: 'The substance that dissolves (e.g. salt).' },
    { term: 'Solvent', definition: 'The substance that does the dissolving (e.g. water).' },
    { term: 'Suspension', definition: 'A mixture with solid particles floating in a liquid.' },
    { term: 'Emulsion', definition: 'A mixture of droplets of one liquid in another (e.g. milk).' },
  ],
  examQuestion: {
    prompt:
      '(a) State TWO differences between a pure substance and a mixture. (b) Explain why melting point can be used to test the purity of a substance. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Pure substance is one element/compound; mixture is two or more substances',
      'Pure substance has sharp melting/boiling point; mixture has a range',
      'A pure substance has a fixed, sharp melting point',
      'If sample melts at the expected fixed value → pure; if over a range → mixed/impure',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-5.5.w1', type: 'text', question: 'A solute dissolved in a solvent forms a ___ .', answer: 'solution', hint1: '8 letters', hint2: 'Clear liquid', hint3: 'Saltwater for example' },
      { id: 'chem-5.5.w2', type: 'choice', question: 'Milk is an example of a…', options: ['Solution', 'Suspension', 'Emulsion', 'Element'], answer: 'Emulsion', hint1: 'Fat droplets in water', hint2: '8 letters', hint3: 'Liquid in liquid' },
      { id: 'chem-5.5.w3', type: 'choice', question: 'Mud + water is a…', options: ['Solution', 'Suspension', 'Emulsion', 'Pure substance'], answer: 'Suspension', hint1: 'Solids settle out', hint2: 'Cloudy', hint3: 'Not dissolved' },
    ],
    level1: [
      { id: 'chem-5.5.1', type: 'choice', question: 'Which is pure?', options: ['Air', 'Sea water', 'Distilled water', 'Mud'], answer: 'Distilled water', hint1: 'Only H₂O molecules', hint2: 'No salts left', hint3: 'Boiled and condensed' },
      { id: 'chem-5.5.2', type: 'text', question: 'In salt water, the solvent is ___ .', answer: 'water', hint1: '5 letters', hint2: 'Does the dissolving', hint3: 'H₂O' },
    ],
    level2: [
      { id: 'chem-5.5.3', type: 'choice', question: 'Why does a mixture melt over a range of temperatures?', options: ['Each substance has its own melting point', 'Mixtures cool down faster', 'It depends on size of pieces', 'None of the above'], answer: 'Each substance has its own melting point', hint1: 'Multiple components', hint2: 'No single value', hint3: 'Different particles' },
      { id: 'chem-5.5.4', type: 'text', question: 'A mixture of fat droplets in water (e.g. milk) is called an ___ .', answer: 'emulsion', hint1: '8 letters', hint2: 'Liquid in liquid', hint3: 'Starts with e' },
    ],
    level3: [
      { id: 'chem-5.5.5', type: 'choice', question: 'A liquid melts at exactly 0.0 °C and boils at exactly 100.0 °C. It is most likely…', options: ['Salt water', 'Pure water', 'Cooking oil', 'Mud'], answer: 'Pure water', hint1: 'Sharp values', hint2: 'Single substance', hint3: 'No salts' },
      { id: 'chem-5.5.6', type: 'choice', question: 'You can separate a mixture using…', options: ['Chemical reactions only', 'Physical methods only', 'Either physical or chemical methods (depends on mixture)', 'Neither'], answer: 'Physical methods only', hint1: 'No chemical change needed', hint2: 'Filter, evaporate, distil', hint3: 'Mechanical separation' },
    ],
  },
};

// =====================================================================
// 5.6  METALS AND NON-METALS
// =====================================================================
const METALS_TOPIC = {
  id: 'chem-5.6',
  title: 'Metals and Non-metals',
  slug: 'metals-and-non-metals',
  pageRef: 'Section 5 · Topic 6',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Properties of metals',
        body:
          'Most elements are metals. Typical properties: shiny when polished; conduct heat and electricity well; malleable (can be hammered into shape); ductile (can be drawn into wires); usually solid at room temperature (mercury is the exception) with high melting points; sonorous (ring when hit). Examples: copper, gold, iron, aluminium.',
        check: {
          question: 'Which is NOT a typical metal property?',
          options: ['Conducts electricity', 'Malleable', 'Brittle when struck', 'Shiny'],
          answer: 'Brittle when struck',
          explainer: 'Most metals are malleable, not brittle.',
        },
      },
      {
        heading: 'Properties of non-metals',
        body:
          'Non-metals tend to be: dull (not shiny); poor conductors of heat and electricity (carbon as graphite is the famous exception); brittle when solid; low melting/boiling points (many are gases at room temperature); not sonorous. Examples: oxygen, sulfur, carbon, nitrogen.',
        check: {
          question: 'A typical non-metal will…',
          options: ['Conduct electricity', 'Be a good insulator', 'Be malleable', 'Be sonorous'],
          answer: 'Be a good insulator',
          explainer: 'Most non-metals are poor conductors — i.e. good insulators.',
        },
      },
      {
        heading: 'Where they are on the periodic table',
        body:
          'On the periodic table, metals are on the LEFT and middle, non-metals on the RIGHT (with hydrogen at the top-left as an exception). A "staircase" line separates them, with elements like silicon and germanium on the line acting as semi-metals (used in computer chips).',
        check: {
          question: 'On the periodic table, non-metals are mostly…',
          options: ['On the far left', 'In the middle', 'On the right side', 'Hidden away'],
          answer: 'On the right side',
          explainer: 'Right side of the table = non-metals (with the noble gases on the very far right).',
        },
      },
    ],
    keyPoints: [
      'Metals: shiny, conduct, malleable, ductile, sonorous, mostly solid',
      'Non-metals: dull, insulators, brittle, often gases at room temp',
      'Mercury is the liquid-metal exception',
      'Carbon (graphite) is the conducting non-metal exception',
      'Periodic table: metals left/middle, non-metals right',
    ],
    workedExamples: [
      {
        problem: 'A shiny grey solid is hit with a hammer and bends rather than shatters. It conducts electricity. Predict whether it is a metal or non-metal.',
        steps: [
          { step: 1, instruction: 'Property check', working: 'Shiny + conducts + malleable' },
          { step: 2, instruction: 'Match', working: 'All three are typical metal properties' },
        ],
        answer: 'Metal.',
        commonMistake: 'Skipping over conductivity — that\u2019s a strong clue.',
      },
    ],
    whyItWorks:
      'Metals share properties because of how their atoms bond — a "sea" of free-moving electrons (you\u2019ll meet metallic bonding at GCSE). That sea is what carries electricity, makes them shiny, and allows them to bend without snapping.',
  },
  glossary: [
    { term: 'Metal', definition: 'An element that is shiny, malleable, ductile and conducts heat/electricity well.' },
    { term: 'Non-metal', definition: 'An element that is usually dull, brittle and a poor conductor.' },
    { term: 'Malleable', definition: 'Can be hammered into shape without breaking.' },
    { term: 'Ductile', definition: 'Can be drawn out into a wire.' },
    { term: 'Conductor', definition: 'A material that lets heat or electricity flow through it.' },
    { term: 'Insulator', definition: 'A material that does NOT let heat or electricity flow easily.' },
  ],
  examQuestion: {
    prompt:
      '(a) State THREE typical properties of metals. (b) Carbon (in the form of graphite) breaks one common non-metal rule — explain. (c) Where on the periodic table would you find non-metals? (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Any 3 of: shiny / conducts heat / conducts electricity / malleable / ductile / sonorous / high melting point',
      'Most non-metals do NOT conduct electricity',
      'Graphite (a form of carbon) DOES conduct electricity',
      'Right hand side of the periodic table',
      'With noble gases on the very far right column',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-5.6.w1', type: 'text', question: 'Metals can be drawn into wires — they are ___ .', answer: 'ductile', hint1: '7 letters', hint2: 'Like copper wire', hint3: 'Starts with d' },
      { id: 'chem-5.6.w2', type: 'choice', question: 'Which is a non-metal?', options: ['Iron', 'Copper', 'Sulfur', 'Aluminium'], answer: 'Sulfur', hint1: 'Yellow brittle solid', hint2: 'Smelly when burnt', hint3: '6 letters' },
      { id: 'chem-5.6.w3', type: 'choice', question: 'Which of these is the only LIQUID metal at room temperature?', options: ['Iron', 'Mercury', 'Gold', 'Copper'], answer: 'Mercury', hint1: 'Old thermometers', hint2: '7 letters', hint3: 'Silvery liquid' },
    ],
    level1: [
      { id: 'chem-5.6.1', type: 'choice', question: 'A material that does NOT conduct electricity well is a(n)…', options: ['Conductor', 'Metal', 'Insulator', 'Catalyst'], answer: 'Insulator', hint1: 'Stops current', hint2: '9 letters', hint3: 'Plastic, rubber' },
      { id: 'chem-5.6.2', type: 'text', question: 'Carbon as ___ is the famous non-metal that DOES conduct electricity.', answer: 'graphite', hint1: '8 letters', hint2: 'Pencil "lead"', hint3: 'Starts with g' },
    ],
    level2: [
      { id: 'chem-5.6.3', type: 'choice', question: 'Which property would help identify a metal?', options: ['Brittle when hit', 'Sonorous (rings)', 'Poor conductor', 'Low melting point'], answer: 'Sonorous (rings)', hint1: 'Like a bell', hint2: 'Used in instruments', hint3: 'Tone when struck' },
      { id: 'chem-5.6.4', type: 'choice', question: 'Where on the periodic table are most metals?', options: ['Right side', 'Left and middle', 'Bottom row only', 'Hidden away'], answer: 'Left and middle', hint1: 'Most of the table', hint2: 'Including the d-block', hint3: 'Group 1, 2 + transition' },
    ],
    level3: [
      { id: 'chem-5.6.5', type: 'choice', question: 'A scientist has a shiny solid that doesn\u2019t conduct electricity. Most likely…', options: ['A typical metal', 'A non-metal', 'Pure water', 'A gas'], answer: 'A non-metal', hint1: 'Misses key property', hint2: 'Shiny but not conducting', hint3: 'Iodine is shiny non-metal' },
      { id: 'chem-5.6.6', type: 'text', question: 'Elements on the metal/non-metal staircase line act as ___-metals.', answer: 'semi', hint1: '4 letters', hint2: 'Half + metal', hint3: 'Used in computer chips' },
    ],
  },
};

// =====================================================================
// 5.7  SEPARATING MIXTURES
// =====================================================================
const SEPARATING_TOPIC = {
  id: 'chem-5.7',
  title: 'Separating Mixtures',
  slug: 'separating-mixtures',
  pageRef: 'Section 5 · Topic 7',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Filtration and evaporation',
        body:
          'Filtration separates an insoluble solid from a liquid (e.g. sand from water). The mixture is poured through filter paper; the solid stays as the residue, the liquid passes through as the filtrate. Evaporation/crystallisation separates a dissolved solid from a solvent (e.g. salt from salt water): heat the solution; the water evaporates and crystals of solid are left behind.',
        check: {
          question: 'Which method separates sand from water?',
          options: ['Distillation', 'Filtration', 'Chromatography', 'Magnet'],
          answer: 'Filtration',
          explainer: 'Sand is insoluble — it stays on the filter paper as the residue.',
        },
      },
      {
        heading: 'Distillation',
        body:
          'Simple distillation separates a solvent from its solution (e.g. pure water from salt water). The solution is heated; vapour is collected and cooled in a condenser; pure liquid drips out. Fractional distillation separates two liquids with different boiling points (e.g. ethanol from water): a fractionating column lets the lower-boiling liquid escape first while higher-boiling liquids condense and drip back.',
        check: {
          question: 'Why is distillation better than filtration for separating salt FROM water (and keeping the water)?',
          options: ['Filter paper would dissolve', 'Salt water passes straight through filter paper', 'Filtration is too slow', 'Salt is heavy'],
          answer: 'Salt water passes straight through filter paper',
          explainer: 'Dissolved salt isn\u2019t trapped by filter paper. Distillation evaporates the water and condenses it back as pure water.',
        },
      },
      {
        heading: 'Chromatography',
        body:
          'Chromatography separates a mixture of dissolved coloured substances (e.g. food dyes in a sweet). A spot of the mixture is placed near the bottom of paper; the bottom dips in a solvent; the solvent rises through the paper carrying each dye different distances depending on how strongly each is attracted to paper vs. solvent. Different dyes show as separate spots — letting you identify or compare mixtures.',
        check: {
          question: 'Chromatography is best for separating…',
          options: ['Sand from water', 'Salt from water', 'Different coloured dyes', 'Two different metals'],
          answer: 'Different coloured dyes',
          explainer: 'It separates dissolved coloured substances based on different attractions to paper vs solvent.',
        },
      },
    ],
    keyPoints: [
      'Filtration: insoluble solid + liquid (sand + water)',
      'Evaporation/crystallisation: dissolved solid from solvent (salt out of water)',
      'Simple distillation: pure solvent from solution (water from salt water)',
      'Fractional distillation: two liquids of different boiling points (ethanol/water)',
      'Chromatography: dissolved coloured substances (dyes)',
    ],
    workedExamples: [
      {
        problem: 'Choose the best method to separate: (i) iron filings from sand, (ii) ink dyes, (iii) salt + water (you want pure water back), (iv) sand + water.',
        steps: [
          { step: 1, instruction: 'Iron + sand', working: 'Iron is magnetic → use a magnet' },
          { step: 2, instruction: 'Ink dyes', working: 'Coloured dissolved substances → chromatography' },
          { step: 3, instruction: 'Salt + water (want water)', working: 'Need solvent back → distillation' },
          { step: 4, instruction: 'Sand + water', working: 'Insoluble solid → filtration' },
        ],
        answer: '(i) magnet, (ii) chromatography, (iii) distillation, (iv) filtration.',
        commonMistake: 'Choosing filtration for salt water — salt water passes through filter paper because the salt is dissolved.',
      },
    ],
    whyItWorks:
      'Each separation technique exploits a different property: insolubility (filtration), dissolved-then-recrystallised (evaporation), boiling point (distillation), and attraction to paper vs. solvent (chromatography). Choosing the right method = matching the property to the technique.',
  },
  glossary: [
    { term: 'Filtration', definition: 'Separating an insoluble solid from a liquid using filter paper.' },
    { term: 'Filtrate', definition: 'The liquid that passes through the filter paper.' },
    { term: 'Residue', definition: 'The solid that stays on the filter paper.' },
    { term: 'Evaporation', definition: 'Heating a solution to leave behind solid crystals as the solvent escapes.' },
    { term: 'Distillation', definition: 'Boiling a solution to evaporate the solvent, then condensing it back to pure liquid.' },
    { term: 'Chromatography', definition: 'Separating dissolved coloured substances by their different attractions to a stationary phase (paper) and moving phase (solvent).' },
  ],
  examQuestion: {
    prompt:
      'A student has a mixture of sand, salt and water. Describe the steps to separate the mixture into pure sand, pure salt crystals, and pure water. (6 marks)',
    maxMarks: 6,
    markSchemePoints: [
      'Filter the mixture: sand stays as residue (1)',
      'Wash and dry the sand to obtain pure sand (1)',
      'The filtrate (salt water) is collected (1)',
      'Distil the filtrate: water evaporates and is condensed → pure water (1)',
      'OR: evaporate to dryness for pure salt crystals (1)',
      'State that crystallisation gives larger pure salt crystals (1)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-5.7.w1', type: 'text', question: 'Sand from water is separated by ___ .', answer: 'filtration', hint1: '10 letters', hint2: 'Uses filter paper', hint3: 'Starts with f' },
      { id: 'chem-5.7.w2', type: 'choice', question: 'Pure water from salt water is best obtained by…', options: ['Filtration', 'Distillation', 'Chromatography', 'Magnet'], answer: 'Distillation', hint1: 'Boil + condense', hint2: '12 letters', hint3: 'Need water back' },
      { id: 'chem-5.7.w3', type: 'choice', question: 'Different food dyes are separated using…', options: ['Filtration', 'Evaporation', 'Chromatography', 'Magnet'], answer: 'Chromatography', hint1: 'Coloured spots', hint2: 'Paper + solvent', hint3: '14 letters' },
    ],
    level1: [
      { id: 'chem-5.7.1', type: 'choice', question: 'Iron filings from sand are best separated using a…', options: ['Filter', 'Magnet', 'Distiller', 'Sieve'], answer: 'Magnet', hint1: 'Iron is magnetic', hint2: '6 letters', hint3: 'Picks up iron only' },
      { id: 'chem-5.7.2', type: 'text', question: 'In filtration, the solid trapped on the paper is the ___ .', answer: 'residue', hint1: '7 letters', hint2: 'Stays behind', hint3: 'Starts with r' },
    ],
    level2: [
      { id: 'chem-5.7.3', type: 'choice', question: 'Fractional distillation works because two liquids…', options: ['Have different colours', 'Have different boiling points', 'Are different volumes', 'Smell different'], answer: 'Have different boiling points', hint1: 'Boiling order', hint2: 'Lower BP escapes first', hint3: 'Used in oil refining' },
      { id: 'chem-5.7.4', type: 'choice', question: 'You want salt crystals from salt water. Best method is…', options: ['Filtration', 'Evaporation/crystallisation', 'Chromatography', 'Magnet'], answer: 'Evaporation/crystallisation', hint1: 'Heat the water away', hint2: 'Crystals form', hint3: 'Don\u2019t need the water back' },
    ],
    level3: [
      { id: 'chem-5.7.5', type: 'choice', question: 'Why doesn\u2019t filtration separate salt from water?', options: ['Salt is bigger than water', 'Salt is dissolved (in solution) and passes through filter paper', 'Filter paper repels salt', 'It is too cold'], answer: 'Salt is dissolved (in solution) and passes through filter paper', hint1: 'Solute size at molecule level', hint2: 'Filter pores too big for ions', hint3: 'Solute moves with solvent' },
      { id: 'chem-5.7.6', type: 'text', question: 'In simple distillation, vapour is cooled to a liquid in a piece of glassware called the ___ .', answer: 'condenser', hint1: '9 letters', hint2: 'Cools vapour', hint3: 'Starts with c' },
    ],
  },
};

// =====================================================================
// 5.8  THE PERIODIC TABLE
// =====================================================================
const PERIODIC_TOPIC = {
  id: 'chem-5.8',
  title: 'The Periodic Table',
  slug: 'periodic-table',
  pageRef: 'Section 5 · Topic 8',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'How the table is built',
        body:
          'The periodic table lists every known element. Elements are arranged in order of increasing atomic number (the number of protons in each atom). Each row is a period; each column is a group. Elements in the SAME group have similar chemical properties because they have the same number of outer (valence) electrons.',
        check: {
          question: 'Elements in the same group have similar properties because they share the same number of…',
          options: ['Protons', 'Neutrons', 'Outer electrons', 'Atoms'],
          answer: 'Outer electrons',
          explainer: 'Chemistry is set by outer electrons; elements in a group share that count.',
        },
      },
      {
        heading: 'Group 1, Group 7, Group 0',
        body:
          'Group 1 = the alkali metals (Li, Na, K…). They are soft, low-density and react vigorously with water (potassium can ignite!). Group 7 = the halogens (F, Cl, Br, I). They are reactive non-metals — chlorine is a green gas, bromine a brown liquid, iodine a dark solid. Group 0 = the noble gases (He, Ne, Ar…). They are unreactive — full outer shell of electrons means they don\u2019t need to bond.',
        check: {
          question: 'Why are noble gases (Group 0) unreactive?',
          options: ['They are too small', 'Their outer electron shell is full', 'They have no neutrons', 'They live deep underground'],
          answer: 'Their outer electron shell is full',
          explainer: 'Full outer shells = no need to gain or lose electrons → no reactions.',
        },
      },
      {
        heading: 'Trends down a group',
        body:
          'For Group 1 (alkali metals), reactivity INCREASES down the group (potassium reacts more vigorously than sodium). For Group 7 (halogens), reactivity DECREASES down the group (fluorine is the most reactive of all). Knowing the trend lets you predict how, say, rubidium will behave with water (even more violently than potassium!).',
        check: {
          question: 'Down Group 1, reactivity…',
          options: ['Stays the same', 'Increases', 'Decreases', 'Disappears'],
          answer: 'Increases',
          explainer: 'Outer electron is further from the nucleus → easier to lose → more reactive metal.',
        },
      },
    ],
    keyPoints: [
      'Periodic table: ~100 elements in order of atomic number',
      'Groups (columns) share outer-electron count → similar properties',
      'Group 1 = alkali metals (reactive metals)',
      'Group 7 = halogens (reactive non-metals)',
      'Group 0 = noble gases (unreactive — full outer shell)',
      'Group 1 reactivity ↑ down; Group 7 reactivity ↓ down',
    ],
    workedExamples: [
      {
        problem: 'Predict the reactivity order with water for: lithium (Li), sodium (Na), potassium (K).',
        steps: [
          { step: 1, instruction: 'Identify group', working: 'All Group 1 (alkali metals)' },
          { step: 2, instruction: 'Apply trend', working: 'Reactivity INCREASES down Group 1' },
          { step: 3, instruction: 'Order', working: 'Li (top, least reactive) < Na < K (most reactive)' },
        ],
        answer: 'Li < Na < K (lithium least reactive; potassium most reactive).',
        commonMistake: 'Reversing the trend — for Group 1, reactivity goes UP as you go down. For Group 7, it goes DOWN.',
      },
    ],
    whyItWorks:
      'The periodic table is the most powerful predictive tool in science. If you know an element\u2019s group, you already know roughly how it will behave — even if it\u2019s an element you\u2019ve never seen before.',
  },
  glossary: [
    { term: 'Atomic number', definition: 'The number of protons in an atom; defines the element.' },
    { term: 'Period', definition: 'A row on the periodic table.' },
    { term: 'Group', definition: 'A column on the periodic table.' },
    { term: 'Alkali metal', definition: 'An element from Group 1 — reactive soft metal.' },
    { term: 'Halogen', definition: 'An element from Group 7 — reactive non-metal.' },
    { term: 'Noble gas', definition: 'An element from Group 0 — full outer shell, unreactive.' },
  ],
  examQuestion: {
    prompt:
      '(a) Why do elements in the same group share similar chemical properties? (b) State the trend in reactivity down Group 1 and explain it briefly. (c) Suggest why Group 0 elements (e.g. argon) are used to fill light bulbs. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Same number of outer (valence) electrons',
      'Group 1: reactivity increases down the group',
      'Outer electron is further from nucleus → easier to lose / atom is bigger',
      'Argon is unreactive (full outer shell)',
      'So the filament does not react/burn out',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-5.8.w1', type: 'text', question: 'A column on the periodic table is called a ___ .', answer: 'group', hint1: '5 letters', hint2: 'Vertical', hint3: 'Starts with g' },
      { id: 'chem-5.8.w2', type: 'choice', question: 'Group 1 elements are called the…', options: ['Halogens', 'Noble gases', 'Alkali metals', 'Transition metals'], answer: 'Alkali metals', hint1: 'Reactive metals', hint2: 'Sodium and potassium', hint3: 'Two words' },
      { id: 'chem-5.8.w3', type: 'choice', question: 'Group 0 elements are called the…', options: ['Halogens', 'Alkali metals', 'Noble gases', 'Transition metals'], answer: 'Noble gases', hint1: 'Full outer shell', hint2: 'Helium, neon', hint3: 'Two words' },
    ],
    level1: [
      { id: 'chem-5.8.1', type: 'choice', question: 'Group 7 elements are called the…', options: ['Halogens', 'Alkali metals', 'Noble gases', 'Transition metals'], answer: 'Halogens', hint1: 'Reactive non-metals', hint2: 'Chlorine, bromine', hint3: '8 letters' },
      { id: 'chem-5.8.2', type: 'choice', question: 'Down Group 7 (halogens), reactivity…', options: ['Increases', 'Stays same', 'Decreases', 'Disappears'], answer: 'Decreases', hint1: 'Opposite of Group 1', hint2: 'Fluorine most reactive', hint3: 'Iodine least' },
    ],
    level2: [
      { id: 'chem-5.8.3', type: 'choice', question: 'Why are alkali metals stored under oil?', options: ['To keep them cool', 'They react with air and water', 'To stop sunlight', 'For decoration'], answer: 'They react with air and water', hint1: 'Group 1 are reactive', hint2: 'Tarnish in air', hint3: 'Fizz/explode in water' },
      { id: 'chem-5.8.4', type: 'text', question: 'The number of protons in an atom = its ___ number.', answer: 'atomic', hint1: '6 letters', hint2: 'Defines the element', hint3: 'Starts with a' },
    ],
    level3: [
      { id: 'chem-5.8.5', type: 'choice', question: 'Predict: rubidium (Rb, Group 1, below K) reacts with water…', options: ['Less than potassium', 'About the same as potassium', 'More vigorously than potassium', 'Not at all'], answer: 'More vigorously than potassium', hint1: 'Continue the trend', hint2: 'Down Group 1 = more reactive', hint3: 'Could explode' },
      { id: 'chem-5.8.6', type: 'choice', question: 'Why is argon used inside filament bulbs?', options: ['It glows brightly', 'It is very cheap', 'It is unreactive — won\u2019t react with hot filament', 'It is very heavy'], answer: 'It is unreactive — won\u2019t react with hot filament', hint1: 'Group 0', hint2: 'Stops oxidation', hint3: 'Inert atmosphere' },
    ],
  },
};

// ---------- assemble the section ----------
const BASE_TOPICS = [
  STATES_TOPIC,
  PARTICLE_THEORY_TOPIC,
  CHANGES_OF_STATE_TOPIC,
  ATOMS_TOPIC,
  MIXTURES_TOPIC,
  METALS_TOPIC,
  SEPARATING_TOPIC,
  PERIODIC_TOPIC,
];

// authoredTopics.json overrides any topic by id (for the PDF pipeline).
export const classifyingMaterialsTopics = BASE_TOPICS.map((t) =>
  AUTHORED_BY_ID[t.id] ? AUTHORED_BY_ID[t.id] : t,
);
